// ChatLayout.js - WhatsApp Desktop Style Layout
// Two-column layout: Left sidebar with project list, Right side with chat

import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { auth, db } from "./firebase.js";
import {
    collection,
    query,
    where,
    onSnapshot,
    orderBy,
    limit,
    getDocs
} from "firebase/firestore";
import ViewTaskPage from "./ViewTaskPage";
import "./ChatLayout.css";

export default function ChatLayout() {
    const { projectId } = useParams();
    const navigate = useNavigate();

    const [projects, setProjects] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [userUID, setUserUID] = useState("");
    const [userEmail, setUserEmail] = useState("");
    const [isMobile, setIsMobile] = useState(false);
    const [sidebarVisible, setSidebarVisible] = useState(true);
    const [projectLastMessages, setProjectLastMessages] = useState({});
    const [loading, setLoading] = useState(true);

    // Check if mobile screen
    useEffect(() => {
        const checkMobile = () => {
            const mobile = window.innerWidth <= 768;
            setIsMobile(mobile);
            // On mobile, hide sidebar when a chat is selected
            if (mobile && projectId) {
                setSidebarVisible(false);
            } else if (!mobile) {
                setSidebarVisible(true);
            }
        };
        checkMobile();
        window.addEventListener("resize", checkMobile);
        return () => window.removeEventListener("resize", checkMobile);
    }, [projectId]);

    // Get current user
    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => {
            if (user) {
                setUserUID(user.uid);
                setUserEmail(user.email || "");
            } else {
                navigate("/");
            }
        });
        return () => unsubscribe();
    }, [navigate]);

    // Fetch projects where user is assigned
    useEffect(() => {
        if (!userUID) return;

        const projectsRef = collection(db, "projects");
        const q = query(
            projectsRef,
            where("users", "array-contains", userUID)
        );

        const unsubscribe = onSnapshot(q, async (snapshot) => {
            const projectsList = snapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
            }));

            setProjects(projectsList);
            setLoading(false);

            // Fetch last message AND all unique senders for each project
            const lastMessages = {};
            for (const project of projectsList) {
                try {
                    const tasksRef = collection(db, "projects", project.id, "tasks");
                    // Fetch last 50 messages to get unique senders for search
                    const tasksQuery = query(tasksRef, orderBy("createdAt", "desc"), limit(50));
                    const tasksSnapshot = await getDocs(tasksQuery);

                    if (!tasksSnapshot.empty) {
                        const lastTask = tasksSnapshot.docs[0].data();

                        // Collect all unique senders for search
                        const allSenders = new Set();
                        const allTexts = [];
                        tasksSnapshot.docs.forEach(doc => {
                            const data = doc.data();
                            if (data.createdByName) allSenders.add(data.createdByName);
                            if (data.createdBy && data.createdBy.includes('@')) {
                                // Extract name part from email
                                allSenders.add(data.createdBy.split('@')[0]);
                            }
                            if (data.text) allTexts.push(data.text);
                        });

                        lastMessages[project.id] = {
                            text: lastTask.text || "",
                            createdBy: lastTask.createdByName || lastTask.createdBy || "",
                            updatedAt: lastTask.updatedAt || lastTask.createdAt,
                            allSenders: Array.from(allSenders), // All unique senders for search
                            allTexts: allTexts, // All message texts for search
                        };
                    }
                } catch (err) {
                    console.error("Error fetching last message for project:", project.id, err);
                }
            }
            setProjectLastMessages(lastMessages);
        });

        return () => unsubscribe();
    }, [userUID]);

    // Handle project selection
    const handleProjectClick = (project) => {
        navigate(`/view/${project.id}/${encodeURIComponent(project.name)}`);
        if (isMobile) {
            setSidebarVisible(false);
        }
    };

    // Format timestamp for sidebar
    const formatSidebarTime = (timestamp) => {
        if (!timestamp) return "";

        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        const now = new Date();
        const diff = now - date;
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));

        if (days === 0) {
            return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
        } else if (days === 1) {
            return "Yesterday";
        } else if (days < 7) {
            return date.toLocaleDateString([], { weekday: "short" });
        } else {
            return date.toLocaleDateString([], { month: "short", day: "numeric" });
        }
    };

    // Get first letter of project name for avatar
    const getProjectInitial = (name) => {
        return name ? name.charAt(0).toUpperCase() : "P";
    };

    // Filter projects by search term (searches project name, all users, and message content)
    const filteredProjects = projects.filter((project) => {
        const search = searchTerm.toLowerCase();
        const lastMessage = projectLastMessages[project.id];

        // Search in project name
        if (project.name?.toLowerCase().includes(search)) return true;

        // Search in all message senders (not just last message)
        if (lastMessage?.allSenders?.some(sender =>
            sender?.toLowerCase().includes(search)
        )) return true;

        // Search in all message texts
        if (lastMessage?.allTexts?.some(text =>
            text?.toLowerCase().includes(search)
        )) return true;

        return false;
    });

    // Truncate last message
    const truncateMessage = (text, maxLength = 35) => {
        if (!text) return "No messages yet";
        return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
    };

    return (
        <div className="whatsapp-layout">
            {/* Mobile menu toggle button */}
            {isMobile && !sidebarVisible && (
                <button
                    className="mobile-menu-toggle"
                    onClick={() => setSidebarVisible(true)}
                >
                    <i className="bi bi-list"></i>
                </button>
            )}

            {/* LEFT SIDEBAR - Project List */}
            <div className={`chat-sidebar ${isMobile && !sidebarVisible ? 'hidden' : ''}`}>
                {/* Sidebar Header */}
                <div className="sidebar-header">
                    <div className="sidebar-header-left">
                        <div className="sidebar-user-avatar">
                            {userEmail ? userEmail.charAt(0).toUpperCase() : "U"}
                        </div>
                        <h1 className="sidebar-app-title">JIS Chat</h1>
                    </div>
                    <div className="sidebar-header-actions">
                        <button
                            className="sidebar-header-btn home-btn"
                            onClick={() => navigate("/dashboard")}
                            title="Go to Dashboard"
                        >
                            <i className="bi bi-house-door"></i>
                        </button>
                    </div>
                </div>

                {/* Search Bar */}
                <div className="sidebar-search">
                    <div className="sidebar-search-wrapper">
                        <i className="bi bi-search sidebar-search-icon"></i>
                        <input
                            type="text"
                            className="sidebar-search-input"
                            placeholder="Search by user or chat..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                {/* Project List */}
                <div className="project-list">
                    {loading ? (
                        <div className="sidebar-empty">
                            <div className="spinner-border text-secondary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : filteredProjects.length === 0 ? (
                        <div className="sidebar-empty">
                            <div className="sidebar-empty-icon">💬</div>
                            <p className="sidebar-empty-text">
                                {searchTerm ? "No projects found" : "No projects yet"}
                            </p>
                        </div>
                    ) : (
                        filteredProjects.map((project) => {
                            const lastMessage = projectLastMessages[project.id];
                            const isActive = projectId === project.id;

                            return (
                                <div
                                    key={project.id}
                                    className={`project-item ${isActive ? 'active' : ''}`}
                                    onClick={() => handleProjectClick(project)}
                                >
                                    <div className="project-avatar">
                                        {getProjectInitial(project.name)}
                                    </div>
                                    <div className="project-info">
                                        <div className="project-info-top">
                                            <span className="project-name">{project.name}</span>
                                            <span className="project-time">
                                                {formatSidebarTime(lastMessage?.updatedAt)}
                                            </span>
                                        </div>
                                        <div className="project-info-bottom">
                                            <span className="project-last-message">
                                                {lastMessage?.createdBy && (
                                                    <span style={{ color: "#6ee7b7", fontWeight: 500 }}>
                                                        {lastMessage.createdBy.split("@")[0]}:{" "}
                                                    </span>
                                                )}
                                                {truncateMessage(lastMessage?.text)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            {/* RIGHT PANEL - Chat Area */}
            <div className="chat-main">
                {projectId ? (
                    // Show ViewTaskPage when a project is selected
                    <ViewTaskPage />
                ) : (
                    // Welcome screen when no project is selected
                    <div className="chat-welcome">
                        <div className="chat-welcome-content">
                            <div style={{ fontSize: "64px", marginBottom: "24px" }}>💬</div>
                            <h2 className="chat-welcome-title">JIS Project Chat</h2>
                            <p className="chat-welcome-text">
                                Select a project from the sidebar to start chatting.
                                <br />
                                Stay connected with your team in real-time.
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
